import {
  createContext,
  useState,
  ReactNode,
  ReactElement,
  useEffect,
  useCallback,
} from 'react'

import { FormValues, initialValues } from '@types'

interface SurveyContextType {
  formData: FormValues
  setFormData: (data: FormValues) => void
  clearFormData: () => void
  isFormDataLoaded: boolean
}
const SurveyContext = createContext<SurveyContextType>({
  formData: initialValues,
  setFormData: () => {},
  clearFormData: () => {},
  isFormDataLoaded: false,
})

const SurveyProvider = ({
  children,
}: {
  children: ReactNode
}): ReactElement => {
  const [formData, setFormDataState] = useState<FormValues>(initialValues)

  const [isFormDataLoaded, setIsFormDataLoaded] = useState(false)

  // Enhanced setFormData with auto-sync to sessionStorage
  const setFormData = useCallback((data: FormValues) => {
    setFormDataState(data)

    try {
      window.sessionStorage.setItem('formValues', JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save form data to session storage:', error)
    }
  }, [])

  const clearFormData = useCallback(() => {
    setFormDataState(initialValues)

    try {
      window.sessionStorage.removeItem('formValues')
    } catch (error) {
      console.error('Failed to clear form data from session storage:', error)
    }
  }, [])

  const restoreFormData = (): void => {
    try {
      const storedData = window.sessionStorage.getItem('formValues')
      if (storedData) {
        const parsedData: FormValues = JSON.parse(storedData)
        setFormDataState(parsedData)
      } else {
        setFormDataState(initialValues)
      }
    } catch (error) {
      console.error('Failed to restore form data:', error)
      window.sessionStorage.removeItem('formValues')
    } finally {
      setIsFormDataLoaded(true)
    }
  }

  // Auto-restore on mount (sessionStorage -> context)
  useEffect(() => {
    restoreFormData()
  }, [])

  return (
    <SurveyContext.Provider
      value={{ formData, setFormData, clearFormData, isFormDataLoaded }}
    >
      {children}
    </SurveyContext.Provider>
  )
}

export { SurveyContext, SurveyProvider }
